import { parseAmount, type PlanCategoryDto, type TargetCadence, type TargetType } from '@koverta/shared';
import { useState } from 'react';
import { Dialog, MoneyInput, Seg } from '../components/ui';
import { del, errorMessage, put } from '../lib/api';
import { useHousehold } from '../lib/household';
import { queryClient } from '../lib/queries';

/** Create / edit a category target (domain-model.md "Target"). */
export function TargetDialog({ c, onClose }: { c: PlanCategoryDto; month: string; onClose: () => void }) {
  const { fmt } = useHousehold();
  const t = c.target;
  const [type, setType] = useState<TargetType>(t?.type ?? 'spending');
  const [amount, setAmount] = useState(t ? fmt.num(t.amount, 2) : '');
  const [cadence, setCadence] = useState<TargetCadence>(t?.cadence ?? 'monthly');
  const [mode, setMode] = useState<'set_aside' | 'refill'>(t?.mode ?? 'set_aside');
  const [dueDate, setDueDate] = useState(t?.dueDate ?? '');
  const [err, setErr] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['plan'] });
  const save = async () => {
    const minor = parseAmount(amount, fmt.locale);
    if (!minor || minor <= 0) return setErr('Enter a target amount');
    try {
      await put(`/api/categories/${c.id}/target`, {
        type,
        amount: minor,
        cadence: type === 'spending' ? cadence : 'monthly',
        mode: type === 'spending' ? mode : 'set_aside',
        dueDate: dueDate || null,
      });
      await refresh();
      onClose();
    } catch (e) {
      setErr(errorMessage(e));
    }
  };
  const remove = async () => {
    await del(`/api/categories/${c.id}/target`);
    await refresh();
    onClose();
  };
  return (
    <Dialog
      title={`${t ? 'Edit' : 'Create'} target · ${c.name}`}
      onClose={onClose}
      size="md"
      footer={
        <>
          {t ? (
            <button className="btn ghost danger" onClick={remove}>
              Delete target
            </button>
          ) : null}
          <span className="sp" />
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={save}>
            Save target
          </button>
        </>
      }
    >
      <Seg
        full
        label="Target type"
        value={type}
        onChange={setType}
        options={[
          ['spending', 'Needed for spending'],
          ['savings_balance', 'Savings balance'],
        ]}
      />
      <label className="field">
        {type === 'savings_balance' ? 'Total to save' : 'Amount'} ({fmt.base})
        <MoneyInput
          className="input r"

          value={amount}
          onChange={setAmount}
          autoFocus
        />
      </label>
      {type === 'spending' ? (
        <>
          <div className="field">
            Every
            <Seg
              value={cadence}
              onChange={setCadence}
              label="Cadence"
              options={[
                ['weekly', 'Week'],
                ['monthly', 'Month'],
                ['yearly', 'Year'],
              ]}
            />
          </div>
          <div className="field">
            Each period
            <Seg
              value={mode}
              onChange={setMode}
              label="Mode"
              options={[
                ['set_aside', 'Set aside another'],
                ['refill', 'Refill up to'],
              ]}
            />
          </div>
        </>
      ) : null}
      {type === 'savings_balance' || (type === 'spending' && cadence === 'yearly') ? (
        <label className="field">
          {type === 'savings_balance' ? 'By date (optional)' : 'Due date'}
          <input className="input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </label>
      ) : null}
      <div className="muted" style={{ fontSize: 12.5 }}>
        {type === 'spending' && mode === 'refill'
          ? 'Each month, top Available up to this amount. Leftovers count towards it.'
          : type === 'spending'
            ? 'Assign this amount every period, whatever is left over. For a steady monthly saving with no end, pick Every Month.'
            : 'Ovce na broju spreads what is left evenly over the months until the date.'}
      </div>
      {err ? <div className="err-t">{err}</div> : null}
    </Dialog>
  );
}
